import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import Chart, {
  ArgumentAxis,
  ValueAxis,
  Series,
  Legend,
  Tooltip,
  CommonSeriesSettings,
} from 'devextreme-react/chart';
import { useEndReasonTypeData } from '../contexts'; 
import { EndReasonTypeFilterControls } from './EndReasonTypeFilterControls';
import { DashboardCard } from '../components/DashboardCard';

const END_REASON_TYPE_COLORS: Record<string, string> = {
  'Drop Out': '#EF5350',
  'Withdrawal': '#FF9800',
  'N/A': '#26A69A',
  'Other': '#FFA726',
  'Graduated': '#66BB6A',
  'Persisted Next Grade': '#42A5F5',
  'Persisted Same Grade': '#5C6BC0',
  'Graduated With Rigorous': '#2E7D32',
  'Graduated Without Rigorous': '#81C784',
  'Deceased': '#757575',
  'Unknown': '#BDBDBD',
};

interface StackedChartDataPoint {
  year: string;
  [key: string]: string | number;
}

interface TooltipArg {
  argument: string;
  seriesName: string;
  value?: number;
  point?: {
    data?: any;
  };
}

const EndReasonTypeChartContent = () => {
  const { data, loading, error } = useEndReasonTypeData();

  if (loading) {
    return <Skeleton variant="rounded" height={400} />;
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography align="center" color="textSecondary">
        No end reason type data available for selected years
      </Typography>
    );
  }

  const prepareChartData = (): { data: StackedChartDataPoint[]; endReasonTypes: string[] } => {
    const years = [...new Set(data.map((d) => d.year))].sort();
    const endReasonTypes = [...new Set(data.map((d) => d.endReasonTypeName))].sort();

    const chartData: StackedChartDataPoint[] = years.map((year) => {
      const yearData: StackedChartDataPoint = { year: `${year}-${year + 1}` };

      endReasonTypes.forEach((type) => {
        const item = data.find((d) => d.year === year && d.endReasonTypeName === type);
        yearData[type] = item?.percentageSubmitted || 0;
      });

      return yearData;
    });

    return { data: chartData, endReasonTypes };
  };

  const { data: chartData, endReasonTypes } = prepareChartData();

  return (
    <Box sx={{ height: '100%' }}>
      <EndReasonTypeFilterControls />
      <Chart dataSource={chartData} height={400}>
      <CommonSeriesSettings 
        type="stackedbar" 
        barPadding={0.3}
      />
        <ArgumentAxis />
        <ValueAxis
          visualRange={{ startValue: 0, endValue: 100 }}
          tickInterval={10}
          title="Percentage (%)"
        />
        <CommonSeriesSettings type="stackedbar" />
        {endReasonTypes.map((type) => (
          <Series
            key={type}
            valueField={type}
            argumentField="year"
            name={type}
            color={END_REASON_TYPE_COLORS[type] || '#9E9E9E'}
            barWidth={100}
          />
        ))}
        <Legend visible={true} horizontalAlignment="center" verticalAlignment="bottom" />
        <Tooltip
          enabled={true}
          customizeTooltip={(arg: TooltipArg) => {
            const yearString = arg.argument;
            const year = parseInt(yearString.split('-')[0]);
            const endReasonType = arg.seriesName;

            const item = data.find((d) => d.year === year && d.endReasonTypeName === endReasonType);

            if (item) {
              return {
                text: `${endReasonType}: ${arg.value?.toFixed(1)}% (${item.studentCount}/${
                  item.totalEnrolled
                } students)`,
              };
            }

            return {
              text: `${endReasonType}: ${arg.value?.toFixed(1)}%`,
            };
          }}
        />
      </Chart>
    </Box>
  );
};

export const EndReasonTypeChartCard = () => (
  <DashboardCard title="End Reason Type Distribution">
    <EndReasonTypeChartContent />
  </DashboardCard>
);